import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { getCurrentUser, signOut } from "@aws-amplify/auth";
import { AuthShell, LoginForm, ForgotPasswordForm, ResetPasswordForm } from "./auth";
import {
  isReturnablePath,
  resolveLoginDestination,
  type AppView,
  type LoginDestination,
} from "./loginRouting";
import {
  CurrentSessionError,
  getCurrentSession,
  type CurrentSession,
} from "./shared/currentSession";
import {
  canAccessOperations,
  getAppDestinations,
  hasMultipleProductAreas,
  recognizeProductPath,
} from "./shared/appDestinations";
import { AppSwitcher } from "./shared/components/AppSwitcher";
import { useAppUpdateCoordinator } from "./shared/hooks/useAppUpdateCoordinator";
import { AreaFallbackNavigationContext, lazyArea } from "./shared/lazyArea";
import { preloadWhenIdle } from "./shared/areaPreload";

// Cada área é carregada sob demanda: o usuário normalmente acessa uma única
// área por sessão, e o bundle inicial fica restrito a login, sessão e seletor
// de aplicativos.
const Dashboard = lazyArea(
  () =>
    import("./distribution").then((module) => ({
      default: module.DistributionDashboard,
    })),
  "Planejamento",
);
const AdminView = lazyArea(
  () =>
    import("./admin").then((module) => ({ default: module.AdminView })),
  "Administração",
);
const LaundryView = lazyArea(
  () =>
    import("./laundry").then((module) => ({ default: module.LaundryView })),
  "Lavanderia",
);
const AuditView = lazyArea(
  () =>
    import("./audit").then((module) => ({ default: module.AuditView })),
  "Auditoria",
);
const InspectionsView = lazyArea(
  () =>
    import("./inspections").then((module) => ({
      default: module.InspectionsView,
    })),
  "Inspeções",
);
const MaintenanceView = lazyArea(
  () =>
    import("./maintenance").then((module) => ({
      default: module.MaintenanceView,
    })),
  "Manutenção",
);
const OperationView = lazyArea(
  () =>
    import("./operations").then((module) => ({ default: module.OperationView })),
  "Operação",
);
const PublicMaintenanceSharePage = lazyArea(
  () =>
    import("./workOrders/PublicMaintenanceSharePage").then((module) => ({
      default: module.PublicMaintenanceSharePage,
    })),
  "a manutenção compartilhada",
);

function LoadingScreen() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen bg-surface font-sans"
    >
      <span className="sr-only">Carregando…</span>
    </div>
  );
}

const defaultPaths: Partial<Record<AppView, string>> = {
  login: "/login",
  dashboard: "/dashboard",
  "admin-dashboard": "/admin/dashboard",
  "staff-camareira": "/operacao/tarefas",
  lavanderia: "/lavanderia/dashboard",
  auditoria: "/auditoria/governanca",
  inspecoes: "/operacao/vistoria-veicular",
  manutencao: "/operacao/manutencao",
  apps: "/aplicativos",
};

function currentPath() {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function initialReturnPath() {
  const path = currentPath();
  return isReturnablePath(path) ? path : null;
}

export function publicMaintenanceTokenFromPath(pathname: string) {
  const match = pathname.match(/^\/compartilhar\/manutencao\/([^/]+)\/?$/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// `access-denied` nao tem destino navegavel; ele e tratado antes de chegar aqui.
type RoutableDestination = Exclude<LoginDestination, { view: "access-denied" }>;

function destinationPath(destination: RoutableDestination) {
  return destination.path || defaultPaths[destination.view];
}

// Antes de resolver a sessao (ou decidir que nao ha uma), a app fica em um
// estado neutro: nem a tela de login nem qualquer conteudo autenticado devem
// aparecer, para nao piscar uma tela errada durante o refresh.
type AppState = "bootstrapping" | AppView;

export default function App() {
  const publicShareToken =
    typeof window === "undefined"
      ? null
      : publicMaintenanceTokenFromPath(window.location.pathname);
  const [view, setView] = useState<AppState>("bootstrapping");
  const [resetEmail, setResetEmail] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [session, setSession] = useState<CurrentSession | null>(null);
  const returnPath = useRef<string | null>(initialReturnPath());

  // Migra automaticamente para a build publicada quando não há trabalho
  // protegido (ex.: inspeção em preenchimento) — issue #49.
  useAppUpdateCoordinator();

  const applyDestination = useCallback((destination: LoginDestination) => {
    if (destination.view === "access-denied") {
      setAccessError(destination.message);
      setView("access-denied");
      return;
    }

    const path = destinationPath(destination);
    if (path && typeof window !== "undefined" && currentPath() !== path) {
      window.history.replaceState({}, "", path);
    }
    setView(destination.view);
  }, []);

  async function resolveAuthenticatedSession() {
    const resolved = await getCurrentSession();
    setSession(resolved);
    applyDestination(resolveLoginDestination(resolved, returnPath.current));
  }

  async function refreshCurrentSession() {
    try {
      const resolved = await getCurrentSession();
      setSession(resolved);
      applyDestination(resolveLoginDestination(resolved, currentPath()));
    } catch (error) {
      if (error instanceof CurrentSessionError) {
        if (error.status === 401) {
          await signOut().catch(() => undefined);
          setSession(null);
          setLoginError("Sua sessão expirou. Entre novamente para continuar.");
          setView("login");
          return;
        }
        setAccessError(error.message);
        setView("access-denied");
        return;
      }
      throw error;
    }
  }

  function openApps() {
    if (!session || !hasMultipleProductAreas(session)) return;
    if (typeof window !== "undefined" && currentPath() !== "/aplicativos") {
      window.history.pushState({}, "", "/aplicativos");
    }
    setView("apps");
  }

  function selectApp(path: string) {
    if (!session) return;
    applyDestination(resolveLoginDestination(session, path));
  }

  useEffect(() => {
    let active = true;

    async function bootstrap() {
      const path = window.location.pathname.toLowerCase();
      if (publicShareToken) return;
      if (path.startsWith("/forgot-password") || path.startsWith("/esqueci-senha")) {
        setView("esqueci-senha");
        return;
      }
      if (path.startsWith("/reset-password")) {
        setView("reset-password");
        return;
      }

      try {
        await getCurrentUser();
        await resolveAuthenticatedSession();
      } catch (error) {
        if (!active) return;
        if (error instanceof CurrentSessionError) {
          if (error.status === 401) {
            await signOut().catch(() => undefined);
            setSession(null);
            setLoginError("Sua sessão expirou. Entre novamente para continuar.");
            setView("login");
            return;
          }
          setAccessError(error.message);
          setView("access-denied");
          return;
        }
        setView("login");
      }
    }

    void bootstrap();
    return () => {
      active = false;
    };
    // This is intentionally a one-time bootstrap; subsequent transitions are
    // driven by the login callback rather than a change to these closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicShareToken]);

  useEffect(() => {
    if (!session) return undefined;

    // O handler so roda depois deste efeito, mas o TypeScript nao mantem o
    // narrowing de `session` dentro dele; guardamos a sessao ja verificada.
    const activeSession = session;

    function handlePopState() {
      applyDestination(resolveLoginDestination(activeSession, currentPath()));
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [applyDestination, session]);

  const activeArea =
    view === "bootstrapping" ? null : recognizeProductPath(currentPath())?.area;

  // Manutenção, Governança e Vistoria Veicular ficam em Operação: quem tem
  // acesso e está em outra área recebe o código em segundo plano, para
  // continuar abrindo essas rotinas se a conexão cair depois.
  useEffect(() => {
    if (!session || view === "bootstrapping" || activeArea === "operations") {
      return undefined;
    }
    if (!canAccessOperations(session)) return undefined;
    return preloadWhenIdle(OperationView.preload);
  }, [activeArea, session, view]);

  async function handleLoginSuccess() {
    setLoginError(null);
    try {
      await resolveAuthenticatedSession();
    } catch (error) {
      if (error instanceof CurrentSessionError) {
        if (error.status === 401) {
          await signOut().catch(() => undefined);
          setSession(null);
          setLoginError("Sua sessão expirou. Entre novamente para continuar.");
          setView("login");
          return;
        }
        setAccessError(error.message);
        setView("access-denied");
        return;
      }
      throw error;
    }
  }

  async function handleLogout() {
    await signOut().catch(() => undefined);
    setSession(null);
    returnPath.current = null;
    setLoginError(null);
    if (typeof window !== "undefined" && currentPath() !== "/login") {
      window.history.replaceState({}, "", "/login");
    }
    setView("login");
  }

  const onOpenApps =
    session && hasMultipleProductAreas(session) ? openApps : undefined;
  const activeProductRoute = recognizeProductPath(currentPath());
  const isOperationRoute = activeProductRoute?.area === "operations";

  if (publicShareToken) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PublicMaintenanceSharePage token={publicShareToken} />
      </Suspense>
    );
  }

  if (view === "bootstrapping") {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-dvh font-sans" data-testid="app-root">
      {view === "login" && (
        <AuthShell>
          <LoginForm
            initialError={loginError}
            onForgotPassword={() => setView("esqueci-senha")}
            onLoginSuccess={handleLoginSuccess}
          />
        </AuthShell>
      )}

      {view === "esqueci-senha" && (
        <AuthShell>
          <ForgotPasswordForm
            onBackToLogin={() => setView("login")}
            onCodeSent={(email) => {
              setResetEmail(email);
              setView("reset-password");
            }}
          />
        </AuthShell>
      )}

      {view === "reset-password" && (
        <AuthShell>
          <ResetPasswordForm
            email={resetEmail}
            onBackToLogin={() => setView("login")}
          />
        </AuthShell>
      )}

      {view === "access-denied" && (
        <AuthShell>
          <div role="alert" className="space-y-5 text-center">
            <p className="text-red-700">
              {accessError || "Não foi possível liberar seu acesso."}
            </p>
            <button
              type="button"
              className="min-h-11 rounded-control px-2 text-sm font-medium text-brand-dark hover:underline focus:outline-none focus:ring-2 focus:ring-focus/30"
              onClick={handleLogout}
            >
              Sair e tentar outra conta
            </button>
          </div>
        </AuthShell>
      )}

      {view === "apps" && (
        <AppSwitcher
          destinations={session ? getAppDestinations(session) : []}
          userName={session?.user.name}
          onSelect={selectApp}
          onLogout={handleLogout}
        />
      )}

      <AreaFallbackNavigationContext.Provider
        value={onOpenApps ? { label: "Aplicativos", onNavigate: onOpenApps } : null}
      >
        <Suspense fallback={<LoadingScreen />}>
          {view === "dashboard" && <Dashboard onLogout={handleLogout} onOpenApps={onOpenApps} />}
          {view === "admin-dashboard" && (
            <AdminView
              onLogout={handleLogout}
              onOpenApps={onOpenApps}
              currentUserSub={session?.user.sub}
              onCurrentUserAccessChanged={refreshCurrentSession}
              startCadastro={
                typeof window !== "undefined" &&
                ["/cadastro", "/admin/cadastro", "/admin/usuarios/novo"].includes(
                  window.location.pathname,
                )
              }
            />
          )}
          {isOperationRoute &&
            session &&
            ["staff-camareira", "inspecoes", "manutencao"].includes(view) && (
              <OperationView
                session={session}
                onLogout={handleLogout}
                onOpenApps={onOpenApps}
              />
            )}
          {view === "lavanderia" && <LaundryView onLogout={handleLogout} onOpenApps={onOpenApps} />}
          {view === "auditoria" && session && (
            <AuditView
              session={session}
              onLogout={handleLogout}
              onOpenApps={onOpenApps}
            />
          )}
          {view === "inspecoes" && !isOperationRoute && (
            <InspectionsView onLogout={handleLogout} onOpenApps={onOpenApps} />
          )}
          {view === "manutencao" && !isOperationRoute && (
            <MaintenanceView onLogout={handleLogout} onOpenApps={onOpenApps} />
          )}
        </Suspense>
      </AreaFallbackNavigationContext.Provider>
    </div>
  );
}
